import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { withoutReservedTags } from "@formant/core/common/withoutReservedTags";
import {
    ICommandParameterModel,
    ICommandSchema,
} from "@formant/core/model/commandTemplate";
import { ICommandTemplate } from "@formant/core/model/ICommandTemplate";
import { ITags } from "@formant/core/model/ITags";
import { ITagSets } from "@formant/core/model/ITagSets";
import {
    Button,
    Form,
    ITypeaheadOption,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    useForm,
    useToast,
} from "@formant/ui-kit";

import { useStores } from "../../../../context/StoreContext";
import { authStore, commandTemplateStore } from "../../../../stores";
import { CommandModalParameters } from "../paramters/Parameters";
import { UnstructuredParameters } from "../paramters/UnstructuredParameters";
import { ChooseFilters } from "../utils/ChooseFilters";
import { CommandTemplateCreateGeneral } from "./CommandTemplateCreateGeneral";

const defaultValues: Omit<ICommandTemplate, "id"> = {
    name: "",
    command: "",
    description: "",
    parameterEnabled: false,
    allowParameterOverride: false,
    parameterValue: null,
    deviceFilter: null,
    enabled: true,
    tags: {},
    schema: JSON.stringify({
        parameters: {},
    } as ICommandSchema),
};

interface ICommandTemplatesCreateProps {
    commandTemplate?: ICommandTemplate;
    onCancel: () => void;
}

export const CommandTemplatesCreate: React.FC<ICommandTemplatesCreateProps> = ({
    commandTemplate,
    onCancel,
}) => {
    const { toast } = useToast();
    const [command] = React.useState<ICommandTemplate | null>(
        commandTemplate || null,
    );
    const [mode, setMode] = React.useState<"create" | "edit">("create");
    const [existingTagsOptions, setExistingTagsOptions] =
        useState<ITypeaheadOption[]>();
    const [activeTab, setActiveTab] = React.useState("general");
    const { deviceStore } = useStores();

    useEffect(() => {
        if (commandTemplate) {
            setMode("edit");
        }
    }, [commandTemplate]);

    useEffect(() => {
        deviceStore
            .tagsAsync({ enabled: true, type: "default" })
            .then(tags => {
                const typeaheadOptions: ITypeaheadOption[] = [];
                for (const key of Object.keys(withoutReservedTags(tags))) {
                    tags[key].forEach(tag => {
                        if (tag) {
                            typeaheadOptions.push({
                                key,
                                value: tag,
                            });
                        }
                        if (
                            !tag &&
                            !typeaheadOptions.find(
                                option => option.key === key,
                            ) &&
                            !typeaheadOptions.find(option => option.key === "*")
                        ) {
                            typeaheadOptions.push({
                                key,
                                value: `*`,
                            });
                        }
                    });
                }
                setExistingTagsOptions(typeaheadOptions);
            })
            .catch(error => {
                console.error("Error fetching tags:", error);
            });
    }, [deviceStore]);

    defaultValues.tags = {
        ...withoutReservedTags(authStore.userTags || {}),
    };
    const form = useForm<ICommandTemplate>({
        defaultValues: command || defaultValues,
    });

    const [parameters, setParameters] = React.useState<
        NonNullable<ICommandSchema["parameters"]>
    >(
        command?.schema && typeof command.schema === "string"
            ? (JSON.parse(command.schema) as ICommandSchema).parameters || {}
            : {},
    );

    const handleTagsChange = (tags: ITypeaheadOption[]) => {
        const tagObject = tags.reduce((acc, tag) => {
            acc[tag.key] = tag.value;
            return acc;
        }, {} as ITags);
        form.setValue("tags", tagObject);
    };

    // Reset form and parameters when modal opens/closes or mode changes
    useEffect(() => {
        if (mode === "create") {
            form.reset(defaultValues);
            setParameters({});
        } else if (mode === "edit" && command) {
            form.reset(command);
            try {
                const schema = command.schema
                    ? (JSON.parse(command.schema) as ICommandSchema)
                    : null;
                setParameters(schema?.parameters || {});
            } catch (e) {
                console.error("Error parsing command schema:", e);
                setParameters({});
            }
        }
    }, [mode, command, form]);

    const handleSubmit = form.handleSubmit(async (data: ICommandTemplate) => {
        const updatedCommand = {
            ...data,
            schema: JSON.stringify({
                parameters,
            } as ICommandSchema),
        };
        if (
            !updatedCommand.parameterEnabled &&
            !updatedCommand.parameterValue
        ) {
            delete updatedCommand.parameterValue;
        }
        const request =
            mode === "create"
                ? commandTemplateStore.create(updatedCommand)
                : commandTemplateStore.update(
                      updatedCommand.id || "",
                      updatedCommand,
                  );
        try {
            await request;
            onCancel();
        } catch (e) {
            const responseError = narrowError(e, ResponseError);
            const validationErrors = responseError.validationErrors;
            for (const key in validationErrors) {
                if (
                    Object.prototype.hasOwnProperty.call(validationErrors, key)
                ) {
                    const errors = validationErrors[key];
                    errors.forEach(error => {
                        toast({
                            title: "Error submitting command template",
                            description: error,
                            variant: "destructive",
                        });
                    });
                }
            }
        }
    });

    const addParameter = () => {
        const id = uuidv4();
        setParameters(prev => ({
            ...prev,
            [id]: {
                id,
                name: "",
                description: "",
                type: "string",
                value: "",
                required: false,
                locked: false,
                allowOverride: true,
                tags: {},
            },
        }));
    };

    const updateParameter = (
        paramId: string,
        updates: Partial<ICommandParameterModel>,
    ) => {
        setParameters(prev => ({
            ...prev,
            [paramId]: { ...prev[paramId], ...updates },
        }));
    };

    const removeParameter = (paramId: string) => {
        setParameters(prev => {
            const newParams = { ...prev };
            delete newParams[paramId];
            return newParams;
        });
    };

    const handleOnCancel = () => {
        onCancel();
    };

    const handleDeviceFilterChange = (tags: ITagSets) => {
        // check if deviceFilter is empty object
        if (Object.keys(tags).length === 0) {
            form.setValue("deviceFilter", {});
        } else {
            form.setValue("deviceFilter", {
                tags,
            });
        }
    };

    return (
        <Form {...form}>
            <form
                onSubmit={handleSubmit}
                className="space-y-4 flex-1 flex flex-col h-full"
            >
                <Tabs
                    className="flex-1 flex flex-col overflow-hidden"
                    value={activeTab}
                    onValueChange={setActiveTab}
                >
                    <TabsList className="px-4 flex justify-start">
                        <TabsTrigger value="general">General</TabsTrigger>
                        <TabsTrigger value="chooseDevices">
                            Choose Devices
                        </TabsTrigger>
                        <TabsTrigger value="parameters">Parameters</TabsTrigger>
                        <TabsTrigger value="unstructuredParameter">
                            Unstructured Parameter
                        </TabsTrigger>
                    </TabsList>

                    <div className="flex-1 overflow-auto px-4">
                        <TabsContent value="general" className="space-y-4 mt-4">
                            <CommandTemplateCreateGeneral
                                form={form}
                                existingTagsOptions={existingTagsOptions}
                                onTagsChange={handleTagsChange}
                            />
                        </TabsContent>

                        <TabsContent value="chooseDevices" className="mt-4">
                            <ChooseFilters
                                existingSelectedTags={
                                    (form.getValues("deviceFilter")
                                        ?.tags as ITagSets) || {}
                                }
                                onSelectedTagsChange={handleDeviceFilterChange}
                            />
                        </TabsContent>

                        <TabsContent value="parameters" className="mt-4">
                            <CommandModalParameters
                                parameters={parameters}
                                updateParameter={updateParameter}
                                removeParameter={removeParameter}
                                addParameter={addParameter}
                            />
                        </TabsContent>
                        <TabsContent
                            value="unstructuredParameter"
                            className="mt-4"
                        >
                            <UnstructuredParameters />
                        </TabsContent>
                    </div>
                </Tabs>
                <div className="p-4 bg-formant-color-background-tertiary flex justify-end gap-4 sticky bottom-0">
                    <Button
                        type="button"
                        size="lg"
                        variant="ghost"
                        onClick={handleOnCancel}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" size="lg">
                        {mode === "create" ? "Create" : "Update"}
                    </Button>
                </div>
            </form>
        </Form>
    );
};
